import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";

import { getPublications } from "../../../services/publicationsServices";

import {
  ensureArray,
  isActivePublication,
  normalizePublication,
  sortPublications,
} from "../utils/publicationsUtils";

export function usePublicationsPage() {
  const { id: routeId } = useParams();
  const bootedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [publications, setPublications] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [zoomImage, setZoomImage] = useState(false);

  const loadPublications = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getPublications();

      const rows = sortPublications(
        ensureArray(response)
          .filter(isActivePublication)
          .map(normalizePublication)
      );

      setPublications(rows);
    } catch (err) {
      console.error("Erreur chargement publications:", err);
      setPublications([]);
      setError("LOAD_ERROR");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    loadPublications();
  }, [loadPublications]);

  useEffect(() => {
    if (!publications.length) {
      setSelectedId(null);
      return;
    }

    if (routeId) {
      const routePublication = publications.find(
        (publication) => String(publication.id) === String(routeId)
      );

      if (routePublication) {
        setSelectedId(routePublication.id);
        return;
      }
    }

    setSelectedId((current) => {
      const stillExists = publications.some(
        (publication) => publication.id === current
      );

      return stillExists ? current : publications[0].id;
    });
  }, [publications, routeId]);

  useEffect(() => {
    setZoomImage(false);
  }, [selectedId]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setZoomImage(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const selectedPublication = useMemo(() => {
    return (
      publications.find((publication) => publication.id === selectedId) ||
      publications[0] ||
      null
    );
  }, [publications, selectedId]);

  const selectPublication = useCallback((id) => {
    setSelectedId(id);

    if (typeof window !== "undefined" && window.innerWidth < 768) {
      window.setTimeout(() => {
        const detail = document.getElementById("publication-detail");
        detail?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 80);
    }
  }, []);

  const openZoom = useCallback(() => {
    setZoomImage(true);
  }, []);

  const closeZoom = useCallback(() => {
    setZoomImage(false);
  }, []);

  const refresh = useCallback(() => {
    setZoomImage(false);
    loadPublications();
  }, [loadPublications]);

  return {
    loading,
    error,
    publications,
    total: publications.length,
    selectedPublication,
    selectedId,
    zoomImage,
    selectPublication,
    openZoom,
    closeZoom,
    refresh,
  };
}